/**
 * Shared B2B storefront helpers. Loaded before the other B2B scripts (defer
 * preserves order) so they can reach window.B2B for locale-aware cart routes,
 * country-scoped URLs, a race-safe header count refresh, money formatting,
 * JSON <script> parsing, and bundle ids, instead of each script re-declaring
 * its own copy.
 *
 * Note: b2b-product.js keeps its own country-aware addItems (richer error
 * parsing via cartAddError); the simple addItems here is what quick-order uses.
 */
;(() => {
  const routesRoot =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/"
  const route = (path) => `${routesRoot.replace(/\/$/, "")}${path}`
  const urls = {
    add: route("/cart/add.js"),
    change: route("/cart/change.js"),
    update: route("/cart/update.js"),
    cartJson: route("/cart.js"),
    cart: route("/cart"),
  }

  const withCountry = (url, country) => {
    const code = String(country || "")
      .trim()
      .toUpperCase()
    if (!/^[A-Z]{2}$/.test(code)) {
      return url
    }
    return `${url}${url.includes("?") ? "&" : "?"}country=${encodeURIComponent(code)}`
  }

  const bundleId = () => {
    if (window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID()
    }
    return `b2b-${Date.now()}-${Math.random().toString(16).slice(2)}`
  }

  const parseJsonScript = (el, fallback = null) => {
    if (!el) {
      return fallback
    }
    try {
      return JSON.parse(el.textContent)
    } catch (_e) {
      return fallback
    }
  }

  const fmtMoney = (cents, format) => {
    const amount = Number(cents || 0) / 100
    const commas = (n, dec) =>
      n.toFixed(dec).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    // Period thousands separator, comma decimal mark (e.g. 1.234,56 / 1.235).
    const dots = (n, dec) =>
      commas(n, dec).replace(/[.,]/g, (c) => (c === "," ? "." : ","))
    return (format || "${{amount}}")
      .replace(/\{\{\s*amount\s*\}\}/g, commas(amount, 2))
      .replace(
        /\{\{\s*amount_no_decimals\s*\}\}/g,
        commas(Math.round(amount), 0)
      )
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, dots(amount, 2))
      .replace(
        /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        dots(Math.round(amount), 0)
      )
  }

  const addItems = async (items) => {
    const res = await fetch(urls.add, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: JSON.stringify({ items }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.description || "Could not add to order.")
    }
    return res.json()
  }

  // Monotonic token so a slow earlier response can't overwrite a newer count.
  let refreshToken = 0
  const refreshCount = async () => {
    const nodes = document.querySelectorAll("[data-cart-count]")
    if (!nodes.length) {
      return
    }
    const token = ++refreshToken
    try {
      const res = await fetch(urls.cartJson, {
        headers: { Accept: "application/json" },
      })
      if (!res.ok || token !== refreshToken) {
        return
      }
      const cart = await res.json()
      if (token !== refreshToken) {
        return
      }
      for (const el of nodes) {
        el.textContent = cart.item_count
        el.toggleAttribute("hidden", cart.item_count <= 0)
      }
    } catch (_e) {
      /* network error: leave the rendered count as-is */
    }
  }

  window.B2B = {
    route,
    urls,
    withCountry,
    bundleId,
    parseJsonScript,
    fmtMoney,
    addItems,
    refreshCount,
  }
})()
